import { ArrowLeft, Command, Download, Redo2, Settings, Undo2 } from 'lucide-react';
import { useState } from 'react';
import { renameProject } from '@ve/core';
import { edit, redo, undo, useEditor } from '../state/editor-store.ts';
import { closeProject, saveNow, useSaveStatus } from '../state/session.ts';
import { useUi } from '../state/ui-store.ts';
import { commandTip, tip } from '../lib/tooltip.ts';

const STATUS_LABEL = {
  saved: 'All changes saved',
  dirty: 'Unsaved changes',
  saving: 'Saving…',
  error: 'Save failed',
};

export function Topbar() {
  const name = useEditor((s) => s.project?.name ?? '');
  const settings = useEditor((s) => s.project?.settings);
  const { canUndo, canRedo, undoLabel, redoLabel } = useEditor();
  const status = useSaveStatus((s) => s.status);
  const lastSavedAt = useSaveStatus((s) => s.lastSavedAt);
  const setDialog = useUi((s) => s.setDialog);
  const [editingName, setEditingName] = useState(false);

  return (
    <header className="topbar">
      <button
        className="btn ghost icon"
        {...tip('Back to all projects (saves first)')}
        onClick={async () => {
          await saveNow();
          closeProject();
          window.location.hash = '';
        }}
      >
        <ArrowLeft size={16} />
      </button>
      {editingName ? (
        <input
          className="input topbar-name-input"
          autoFocus
          defaultValue={name}
          onBlur={(e) => {
            setEditingName(false);
            const v = e.target.value;
            if (v.trim() && v !== name) edit('Rename project', (p) => renameProject(p, v));
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
            if (e.key === 'Escape') setEditingName(false);
          }}
        />
      ) : (
        <button
          className="topbar-name"
          onDoubleClick={() => setEditingName(true)}
          {...tip('Double-click to rename the project')}
        >
          {name}
        </button>
      )}
      {settings && (
        <button
          className="btn ghost small topbar-settings"
          onClick={() => setDialog('settings')}
          {...tip('Resolution and frame rate — click to change')}
        >
          {settings.width}×{settings.height} ·{' '}
          {(settings.fps.num / settings.fps.den).toFixed(settings.fps.den === 1 ? 0 : 2)} fps
        </button>
      )}
      <span
        className={`save-status ${status}`}
        {...tip(
          lastSavedAt
            ? `Saved automatically · last save ${new Date(lastSavedAt).toLocaleTimeString()}`
            : 'Changes are saved automatically',
          'Mod+S',
        )}
      >
        {STATUS_LABEL[status]}
      </span>
      <div className="spacer" />
      <button
        className="btn ghost icon"
        disabled={!canUndo}
        onClick={undo}
        {...commandTip('undo', canUndo ? `Undo ${undoLabel ?? ''}` : 'Nothing to undo')}
      >
        <Undo2 size={16} />
      </button>
      <button
        className="btn ghost icon"
        disabled={!canRedo}
        onClick={redo}
        {...commandTip('redo', canRedo ? `Redo ${redoLabel ?? ''}` : 'Nothing to redo')}
      >
        <Redo2 size={16} />
      </button>
      <button
        className="btn ghost icon"
        onClick={() => setDialog('palette')}
        {...commandTip('palette', 'Search all commands')}
      >
        <Command size={16} />
      </button>
      <button className="btn ghost icon" onClick={() => setDialog('settings')} {...tip('Project settings')}>
        <Settings size={16} />
      </button>
      <button
        className="btn primary"
        onClick={() => setDialog('export')}
        {...commandTip('export', 'Export the timeline as an MP4 video')}
      >
        <Download size={14} /> Export
      </button>
    </header>
  );
}
